import { useState, useEffect } from "react";
import { Calendar, User, ArrowRight } from "lucide-react";
import { BlogPost } from "@/types/blogPost";
import { supabase } from "@/integrations/supabase/client";

interface FeaturedArticleProps {
  featuredPost?: BlogPost | null;
  onReadPost?: (post: BlogPost) => void;
}

export function FeaturedArticle({ featuredPost, onReadPost }: FeaturedArticleProps) {
  const [loading, setLoading] = useState(!featuredPost);
  const [currentFeaturedPost, setCurrentFeaturedPost] = useState<BlogPost | null>(featuredPost || null);

  useEffect(() => {
    if (featuredPost !== undefined) {
      setCurrentFeaturedPost(featuredPost);
      setLoading(false);
      return;
    }

    fetchFeaturedPost();
  }, [featuredPost]);

  const fetchFeaturedPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          blog_categories (
            id,
            name,
            color_hex,
            border_color_hex,
            text_color_hex
          )
        `)
        .eq('published', true)
        .eq('is_featured', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setCurrentFeaturedPost(data || null);
    } catch (error) {
      console.error('Error fetching featured post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReadPost = () => {
    if (currentFeaturedPost && onReadPost) {
      onReadPost(currentFeaturedPost);
    }
  };

  if (loading) {
    return (
      <section className="py-8 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center">
            <p className="text-gray-600">Carregando artigo em destaque...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!currentFeaturedPost) {
    return null;
  }

  return (
    <section className="py-8 sm:py-16">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg p-6 sm:p-8 text-white mb-12 sm:mb-16">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div>
              <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-accent mb-4 inline-block">
                Artigo em Destaque
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-primary mb-4">
                {currentFeaturedPost.title}
              </h2>
              <p className="text-green-100 mb-6 font-text text-base sm:text-lg">
                {currentFeaturedPost.excerpt || 
                 (currentFeaturedPost.content.length > 150 
                   ? currentFeaturedPost.content.substring(0, 150) + "..." 
                   : currentFeaturedPost.content)
                }
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 mb-6">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="text-sm">
                    {currentFeaturedPost.created_at ? 
                      new Date(currentFeaturedPost.created_at).toLocaleDateString('pt-BR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      }) : 
                      "15 de Março, 2024"
                    }
                  </span>
                </div>
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  <span className="text-sm">Dra Brenda Ramos</span>
                </div>
              </div>
              <button 
                onClick={handleReadPost}
                className="bg-white text-green-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-accent hover:bg-green-50 transition-colors flex items-center text-sm sm:text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Ler Artigo Completo
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
            <div className="bg-green-500 rounded-lg h-48 sm:h-64 flex items-center justify-center">
              {currentFeaturedPost.featured_image_url ? (
                <img
                  src={currentFeaturedPost.featured_image_url}
                  alt={currentFeaturedPost.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="text-center">
                  <div className="w-16 sm:w-20 h-16 sm:h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-green-600 text-xl sm:text-2xl font-bold">📝</span>
                  </div>
                  <p className="text-green-100 text-sm sm:text-base">Imagem do Artigo</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
